import {
  ArrowLeftOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  DollarOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  MoreOutlined,
  HistoryOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  PrinterOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { collectionAPIWithErrorHandling } from "../../../api/collection/collection";
import { paymentAPIWithErrorHandling } from "../../../api/payment/payment";
import { receiptAPIWithErrorHandling } from "../../../api/receipt/receipt";
import { mapZnsAPIWithErrorHandling } from "../../../api/zns/zns";
import { downloadBlob } from "../../../utils/excel";
// import ModalHistorySendMessage from "./ModalHistorySendMessage";
import { invoiceAPIWithErrorHandling } from "../../../api/invoice/invoice";
import useReceiptExport from "./useReceiptExport";
import { confirmAction, confirmDelete } from "../../../components/common/confirmDialog";
import { readSwrCache, writeSwrCache, SWR_MAX_AGE_SHORT } from "../../../utils/swrCache";
import { usePermissions } from "../../../utils/hasPermission";
import { neutral, semantic } from "../../../theme/tokens";
import SendZnsDialog from "./SendZnsDialog";
import ExtendPaymentDialog from "./ExtendPaymentDialog";
import { DeadlineTag, OverduePaymentAlert } from "./CollectionDeadline";

/** Khoản phải thu 0đ còn hiệu lực — dữ liệu bất thường support cần soi. Bỏ qua khoản đã hủy. */
const isZeroFee = (fee) => fee.status !== "CANCELLED" && Number(fee.amount_due || 0) === 0;

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Tách riêng để đường cache và đường API dùng chung đúng một cách map.
//
// KHOẢN THU ≠ KHÁCH HÀNG: một khách nhiều tài sản có nhiều khoản trong cùng đợt.
// `total_customers` của backend thực ra là số KHOẢN (COUNT(*)) — tên cũ giữ lại vì
// Excel xuất ra còn đọc. Các số total_paid / pending / cancelled / overdue /
// overpaid_customers cũng đếm theo KHOẢN.
const mapCollectionStats = (statsData) => ({
  totalFees: statsData.total_fees ?? statsData.total_customers ?? 0,
  totalUniqueCustomers: statsData.total_unique_customers || 0,
  totalAmountDue: parseFloat(statsData.total_amount_due || 0),
  totalAmountPaid: parseFloat(statsData.total_amount_paid || 0),
  // Backend cộng GREATEST(...) theo từng dòng. KHÔNG tự tính totalAmountDue - totalAmountPaid:
  // amount_paid được phép vượt amount_due nên phép trừ trên số tổng sẽ để tiền thu vượt của
  // khách này cấn trừ mất tiền còn nợ của khách khác.
  totalOutstanding: parseFloat(statsData.total_outstanding || 0),
  totalOverpaid: parseFloat(statsData.total_overpaid || 0),
  totalOverpaidCustomers: statsData.total_overpaid_customers || 0,
  totalPending: statsData.total_pending || 0,
  totalPaid: statsData.total_paid || 0,
  totalCancelled: statsData.total_cancelled || 0,
  totalOverdue: statsData.total_overdue || 0,
  collectionRate: statsData.collection_rate || 0,
});

export default function InfoCollection() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [collection, setCollection] = useState(null);
  const [extendOpen, setExtendOpen] = useState(false);
  // Logic xuất phiếu thu dùng chung với trang chi tiết đợt thu - xem useReceiptExport.
  const { queueExport, exporting, openHistory, historyModal } = useReceiptExport({
    // Lịch sử lọc theo đợt thu CHA. Đợt thu độc lập không có cha thì lấy theo chính nó.
    rootId: collection?.parent_id || id,
  });
  // SWR: seed 4 card thống kê từ cache của phiên (đọc đúng 1 lần lúc mount) để có số
  // ngay ở lần render đầu, rồi revalidate ở nền trong fetchCollectionStats().
  const statsCacheKey = `collection.stats.${id}`;
  const [stats, setStats] = useState(() => {
    const cached = readSwrCache(statsCacheKey, { maxAge: SWR_MAX_AGE_SHORT });
    return cached ? mapCollectionStats(cached) : {};
  });
  // Đã có số để vẽ thì lần revalidate không bật spinner nữa, tránh 4 card nháy trắng.
  const hasCachedStats = useRef(Object.keys(stats).length > 0);
  const [isExporting, setIsExporting] = useState(false);
  const [sendOneLoading, setSendOneLoading] = useState(null);
  // Đợt đang mở hộp thoại gửi tin cả đợt; null = đóng
  const [sendTarget, setSendTarget] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payModalVisible, setPayModalVisible] = useState(false);
  const [selectedCustomerFee, setSelectedCustomerFee] = useState(null);

  // Thu thủ công mặc định là thu đủ phần còn lại; sửa số tiền (thu lẻ) cần quyền riêng.
  const { can } = usePermissions();
  const canEditPayAmount = can("payments.manualPayment.amount");
  // Xóa khoản thu chỉ hiện với người được cấp đích danh; việc thường ngày dùng Hủy khoản thu.
  const canDeleteFee = can("collections.deleteFee");
  // const [openModalHistorySendMessage, setOpenModalHistorySendMessage] = useState(false);
  // const [customerFeeId, setCustomerFeeId] = useState(null)
  const [payForm] = Form.useForm();
  const [statusFilter, setStatusFilter] = useState("ALL");
  // Lọc khoản phải thu 0đ — cộng dồn với lọc trạng thái, không thay thế.
  const [zeroOnly, setZeroOnly] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Phân trang bảng khoản thu giữ ở đây chứ không để Table tự giữ: mỗi lần tải lại dữ liệu
  // (sau gửi tin, hủy, thu tiền...) cả màn chuyển sang Spin nên Table bị dựng lại, mất lựa
  // chọn số dòng/trang và quay về trang 1. Trước đây còn ghim pageSize: 10 nên chọn 50/trang
  // cũng không có tác dụng.
  const [feePagination, setFeePagination] = useState({ current: 1, pageSize: 10 });

  // Đổi bộ lọc / từ khóa thì về trang 1, tránh đứng ở trang không còn dữ liệu
  useEffect(() => {
    setFeePagination((p) => (p.current === 1 ? p : { ...p, current: 1 }));
  }, [statusFilter, zeroOnly, searchText]);

  const [editFeeModalVisible, setEditFeeModalVisible] = useState(false);
  const [editFeeLoading, setEditFeeLoading] = useState(false);
  const [editFeeForm] = Form.useForm();
  const [editFeePreviewTotal, setEditFeePreviewTotal] = useState(0);

  const { user } = useSelector((state) => state.persistedReducer.user);
  const userType = user?.data?.info?.type;
  const isStaffScopedUser = userType === "STAFF" || userType === "BRANCH_MANAGER";
  const isCollectionLocked = collection?.is_locked === true || collection?.is_locked === 1;
  // const canEditCollection =
  //   Boolean(collection) &&
  //   // OLD: chỉ cho chỉnh khi PLANNED
  //   collection.status === "PLANNED" &&
  //   // NEW: cho chỉnh cả khi ONGOING, chặn khi CLOSED
  //   // collection.status !== "CLOSED" &&
  //   (!isStaffScopedUser || (collection.type_child === "CHILD" && !isCollectionLocked));
  const canEditCollection =
    Boolean(collection) &&
    collection.status !== "CLOSED" &&
    ((!isStaffScopedUser || (collection.type_child === "CHILD" && !isCollectionLocked))
      || isStaffScopedUser && collection.type_child === "PARENT");

  const canDeleteCollection =
    Boolean(collection) &&
    !isStaffScopedUser &&
    !(collection.type_child === "CHILD" && isCollectionLocked);

  const watchEditFees = Form.useWatch('fees', editFeeForm);
  const watchEditDiscount = Form.useWatch('discount_override', editFeeForm);

  useEffect(() => {
    if (watchEditFees) {
      const sumFees = Object.values(watchEditFees).reduce((sum, val) => sum + Number(val || 0), 0);
      const dist = Number(watchEditDiscount || 0);
      setEditFeePreviewTotal(Math.max(0, sumFees - dist));
    }
  }, [watchEditFees, watchEditDiscount]);

  const editCustomerFee = (record) => {
    // 1. Chặn tuyệt đối nếu đã thanh toán
    if (record.status === "PAID" || Number(record.amount_paid) > 0) {
      message.error("Khoản thu này đã được thanh toán một phần hoặc toàn bộ. Không thể chỉnh sửa trực tiếp số tiền!");
      return;
    }

    // 2. Chặn nếu có giao dịch đang treo (VCB/Base64)
    if (record.base64 || record.billNumber || record.paymentRef) {
       // Nếu chỉ là billNumber/paymentRef thì có thể là đã in, nhưng nếu có base64 là đang có QR
       if (record.base64) {
          message.warning("Khoản thu đang có mã QR hoạt động. Vui lòng Hủy giao dịch trước khi sửa!");
          return;
       }
    }

    const startEditing = () => {
      setSelectedCustomerFee(record);
      
      // Prepare the definitions
      const fees = record.fees || {};
      const defaultFees = {};
      if (collection?.fee_definitions) {
         collection.fee_definitions.forEach(fd => {
             defaultFees[fd.id] = fees[fd.id] || 0;
         });
      }

      const discount = Number(record.discount_override || 0);

      editFeeForm.setFieldsValue({
        charge_target_number: record.charge_target_number || "",
        charge_target_number_one: record.charge_target_number_one || "",
        charge_target_number_two: record.charge_target_number_two || "",
        discount_override: discount,
        fees: defaultFees,
      });
      
      const sumFees = Object.values(defaultFees).reduce((a, b) => a + Number(b), 0);
      setEditFeePreviewTotal(Math.max(0, sumFees - discount));

      setEditFeeModalVisible(true);
    };

    // 3. Cảnh báo nếu đã tương tác (Đã gửi tin nhắn hoặc đã in phiếu)
    if (record.is_sent || record.billNumber) {
        Modal.confirm({
            title: 'Cảnh báo thay đổi dữ liệu',
            content: (
                <div>
                    <p>Khoản thu này đã được <b>gửi thông báo</b> hoặc <b>in phiếu</b> cho khách hàng.</p>
                    <p>Việc thay đổi số tiền lúc này sẽ khiến dữ liệu khách hàng cầm bị sai lệch so với hệ thống.</p>
                    <p style={{ color: '#A8681E' }}><b>Gợi ý:</b> Sau khi sửa, bạn nên gửi lại tin nhắn cập nhật cho khách hàng này.</p>
                </div>
            ),
            okText: 'Tiếp tục sửa',
            cancelText: 'Quay lại',
            onOk: startEditing
        });
    } else {
        startEditing();
    }
  };

  const handleEditFeeSubmit = async (values) => {
      const isSensitive = selectedCustomerFee.is_sent || selectedCustomerFee.billNumber;
      
      Modal.confirm({
        title: 'Xác nhận lưu thay đổi',
        content: (
            <div>
                <p>Bạn có chắc chắn muốn cập nhật thông tin khoản thu này không?</p>
                {isSensitive && (
                    <Alert
                        type="warning" 
                        showIcon 
                        message="Hệ thống sẽ ghi vết (Audit Log) việc thay đổi số tiền sau khi đã gửi thông báo."
                        style={{ marginTop: 8 }}
                    />
                )}
            </div>
        ),
        okText: 'Xác nhận lưu',
        cancelText: 'Hủy',
        onOk: async () => {
            try {
              setEditFeeLoading(true);
              // Call API
              await collectionAPIWithErrorHandling.updateCustomerFee(selectedCustomerFee.id, values);
              
              message.success("Cập nhật khoản thu thành công!");
              setEditFeeModalVisible(false);
              fetchCollectionData();
              fetchCollectionStats();
            } catch (error) {
              message.error(error?.message || "Cập nhật thất bại!");
            } finally {
              setEditFeeLoading(false);
            }
        }
      });
  };

  // Filtered customer fees based on status filter and search text
  const filteredCustomerFees = useMemo(() => {
    if (!collection?.customer_fees) return [];
    let data = [...collection.customer_fees];

    // Filter by payment status
    if (statusFilter !== "ALL") {
      data = data.filter((item) => item.status === statusFilter);
    }

    if (zeroOnly) {
      data = data.filter(isZeroFee);
    }

    // Filter by search text (customer name, phone, or asset)
    if (searchText.trim()) {
      const keyword = searchText.trim().toLowerCase();
      data = data.filter((item) => {
        const name = (item.customer?.name || "").toLowerCase();
        // Tên trên khoản thu (tên chủ hộ khi tài sản bật cờ) — tìm được theo tên trên tin nhắn
        const feeName = (item.customer_name || "").toLowerCase();
        const phone = (item.customer?.phone || "").toLowerCase();
        const representName = (item.customer?.represent_name || "").toLowerCase();
        // return name.includes(keyword) || phone.includes(keyword) || representName.includes(keyword);
        const chargeTargetNumber = (item.charge_target_number || "").toLowerCase();
        return name.includes(keyword) || feeName.includes(keyword) || phone.includes(keyword) || representName.includes(keyword) || chargeTargetNumber.includes(keyword);
      });
    }

    return data;
  }, [collection?.customer_fees, statusFilter, zeroOnly, searchText]);

  // Fetch collection data on component mount
  useEffect(() => {
    if (id) {
      fetchCollectionData();
      fetchCollectionStats();
    }
  }, [id]);

  const fetchCollectionData = async () => {
    try {
      setLoading(true);
      const response = await collectionAPIWithErrorHandling.getCollectionById(
        id
      );
      const collectionData = response.data?.data;

      if (!collectionData) {
        message.error("Không tìm thấy đợt thu");
        navigate("/collections");
        return;
      }

      setCollection(collectionData);
    } catch (error) {
      message.error("Không thể tải thông tin đợt thu: " + error.message);
      navigate("/collections");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionStats = async () => {
    try {
      // SWR: đã vẽ được bằng bản cache thì không bật spinner nữa, tránh 4 card nháy
      // trắng mỗi lần vào lại màn.
      setStatsLoading(!hasCachedStats.current);
      const response = await collectionAPIWithErrorHandling.getCollectionStats(
        id
      );
      const statsData = response.data?.data;

      if (statsData) {
        setStats(mapCollectionStats(statsData));
        writeSwrCache(statsCacheKey, statsData);
        hasCachedStats.current = true;
      }
    } catch (error) {
      console.error("Không thể tải thống kê:", error.message);
      // Không hiển thị lỗi để không làm gián đoạn UX, chỉ log.
      // Giữ nguyên stats đang vẽ (có thể là bản cache) thay vì xoá về 0.
    } finally {
      setStatsLoading(false);
    }
  };

  // Get collection status info

  const getStatusInfo = () => {
    if (!collection)
      return { status: "default", text: "Không xác định", color: "gray" };

    const statusConfig = {
      PLANNED: {
        icon: <ClockCircleOutlined />,
        color: "blue",
        text: "Nháp",
      },
      ONGOING: {
        icon: <CheckCircleOutlined />,
        color: "green",
        text: "Đang diễn ra",
      },
      CLOSED: {
        icon: <CheckCircleOutlined />,
        color: "red",
        text: "Đã kết thúc",
      },
    };

    return statusConfig[collection.status];
  };

  // Define table columns for customer fees
  const getTableColumns = () => {
    if (!collection || !collection.customer_fees?.length) return [];

    // Lấy fee_types từ fee_definitions
    const feeDefinitions = collection.fee_definitions || [];

    return [
      {
        title: "Mã",
        key: "code",
        width: "8%",
        fixed: "left",
        render: (_, record) => (
          <div>
            <Tag>{record.code}</Tag>
          </div>
        ),
      },
      {
        title: "Khách hàng",
        key: "customer",
        width: "10%",
        fixed: "left",
        render: (_, record) => {
          // Tên trên khoản thu = tên đi trên tin ZNS, phiếu thu, hóa đơn: có customer_name
          // (tài sản bật cờ chủ hộ) thì lấy nó, không thì tên khách hàng.
          const customerName = record.customer?.name;
          const isChuHo = !!record.customer_name && record.customer_name !== customerName;

          return (
            <div onClick={() => handleCusInfo(record)}
              style={{
                  cursor: "pointer",
                  color: "#0070D7",
              }}
            >
              <div className="font-medium">
                {record.customer_name || customerName || `Khách hàng ${record.customer_id}`}
              </div>
              {isChuHo && (
                <Text type="secondary" className="text-xs block">
                  Tên chủ hộ · KH: {customerName}
                </Text>
              )}
              {record.customer?.phone && (
                <Text type="secondary" className="text-xs">
                  {[record.customer.represent_name, record.customer.phone]
                  .filter(Boolean)
                  .join(" - ")}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        title: "Đơn vị",
        dataIndex: "org_id",
        key: "org_id",
        width: 200,
        render: (_, record) => {
          return (
            <div onClick={() => handleOrgInfo(collection)}>
              <Text style={{ cursor: 'pointer', color: "#0070D7" }}>
                {collection.organization?.name}
              </Text>
            </div>
          );
        }
      },
      {
        title: "Tài sản",
        key: "charge_target_number",
        width: 200,
        align: "center",
        render: (_, record) => (
          <div>
            <div className="font-medium">{record.charge_target_number}</div>
          </div>
        ),
      },
      ...feeDefinitions.map((fd) => ({
        title: (
          <Space direction="vertical" size={0}>
          <Space>
            <span
              className="cursor-pointer hover:text-blue-600 hover:underline"
            >
              {fd.name}
            </span>
          </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>
              (Thuế: {fd.tax ?? 0 }% _ Đơn vị: {fd?.unit})
            </Text>
          </Space>
        ),
        key: fd.id,
        width: 200,
        align: "right",
        render: (_, record) => {
          const amount = record.fees?.[fd.id] || 0;
          return (
            <Text
              className={
                amount > 0 ? "font-medium text-green-600" : "text-gray-400"
              }
            >
              {amount.toLocaleString("vi-VN")} ₫
            </Text>
          );
        },
      })),
      {
        title: "Giảm trừ",
        key: "discount_override",
        width: 250,
        align: "right",
        render: (_, record) => {
          const discount_override = Number(record.discount_override || 0);
          return (
            <Text strong className="text-blue-600">
              {discount_override.toLocaleString("vi-VN")} ₫
            </Text>
          );
        },
      },
      {
        title: "Tổng cộng",
        key: "total",
        width: 160,
        align: "right",
        render: (_, record) => {
          const total = Number(record.amount_due || 0);
          return (
            <Text strong style={{ color: '#0070D7', fontVariantNumeric: 'tabular-nums' }}>
              {total.toLocaleString("vi-VN")} ₫
            </Text>
          );
        },
      },
      {
        title: "Đã thanh toán",
        key: "amount_paid",
        width: 160,
        align: "right",
        render: (_, record) => {
          const paid = Number(record.amount_paid || 0);
          return (
            <Text strong style={{ color: paid > 0 ? '#128555' : '#64748B', fontVariantNumeric: 'tabular-nums' }}>
              {paid.toLocaleString("vi-VN")} ₫
            </Text>
          );
        },
      },
      {
        title: "Còn lại",
        key: "remaining",
        width: 160,
        align: "right",
        render: (_, record) => {
          const remaining = Math.max(0, Number(record.amount_due || 0) - Number(record.amount_paid || 0));
          if (remaining === 0) return (
            <Text style={{ color: '#128555', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>✓ Xong</Text>
          );
          return (
            <Text strong style={{ color: '#E5065C', fontVariantNumeric: 'tabular-nums' }}>
              {remaining.toLocaleString("vi-VN")} ₫
            </Text>
          );
        },
      },
      {
        // amount_paid giữ nguyên tổng thực nhận nên có thể vượt amount_due (khách chuyển dư,
        // hoặc gõ sai số khi thu tiền mặt). Cột này để tìm ra đúng khách cần đối soát trả lại
        // — trước đây phần vượt bị ghi đè nên không nhìn thấy ở đâu cả.
        title: "Thu vượt",
        key: "overpaid",
        width: 140,
        align: "right",
        render: (_, record) => {
          const overpaidAmount = Math.max(0, Number(record.amount_paid || 0) - Number(record.amount_due || 0));
          if (overpaidAmount === 0) return <Text type="secondary">—</Text>;
          return (
            <Tooltip title="Khách đã trả nhiều hơn số phải thu, cần đối soát trả lại">
              <Text strong style={{ color: '#A8681E', fontVariantNumeric: 'tabular-nums' }}>
                +{overpaidAmount.toLocaleString("vi-VN")} ₫
              </Text>
            </Tooltip>
          );
        },
      },
      {
        title: "Tài khoản thu",
        key: "account",
        width: 250,
        align: "right",
        render: (_, record) => {
          const text = record?.account?.name || "_";
          return (
            <Tag color="default">
              {text}
            </Tag>
          );
        },
      },
      {
        title: "Trạng thái",
        key: "status",
        width: 200,
        align: "center",
        fixed: "right",
        render: (_, record) => {
          const statusLabels = {
            PAID: "Đã thanh toán",
            CANCELLED: "Đã hủy",
            UNPAID: "Chưa thanh toán",
            PARTIAL: "Một phần",
          };

          const statusColors = {
            PAID: "green",
            CANCELLED: "red",
            UNPAID: "orange",
            PARTIAL: "blue",
          };

          const tag = (
            <Tag color={statusColors[record.status] || "default"}>
              {statusLabels[record.status] || record.status}
            </Tag>
          );

          // Show info icon with paid_date tooltip for PAID records
          if (record.status === "PAID" && record.paid_date) {
            return (
              <Space size={4}>
                {tag}
                <Tooltip
                  title={`Thanh toán lúc: ${dayjs(record.paid_date).format("DD/MM/YYYY HH:mm")}`}
                  color="#128555"
                >
                  <InfoCircleOutlined style={{ color: "#128555", cursor: "pointer" }} />
                </Tooltip>
              </Space>
            );
          }

          return tag;
        },
      },
      {
        title: "Thao tác",
        key: "actions",
        width: "5%",
        fixed: "right",
        render: (_, record) => {
          const isLocked = ["PAID", "CANCELLED"].includes(record.status) || collection.status === "PLANNED";
          
          const dropdownMenuItems = [
             {
                 key: 'cancel',
                 label: 'Hủy giao dịch',
                 icon: <RollbackOutlined />,
                 danger: true,
                 disabled: record.status === "PAID" || Number(record.amount_paid) > 0 || record.status === "CANCELLED" || collection.status === "PLANNED",
                 onClick: () => showConfirmCancelInvoice(record),
             },
             {
                 key: 'cancel_fee',
                 label: 'Hủy khoản thu',
                 icon: <StopOutlined />,
                 danger: true,
                //  disabled: record.status === "PAID" || Number(record.amount_paid) > 0 || collection.status === "PLANNED",
                 disabled: record.status === "PAID" || record.status === "CANCELLED" || Number(record.amount_paid) > 0,
                 onClick: () => showConfirmCancelCustomerFee(record),
             },
             {
                 key: 'edit',
                 label: 'Sửa thông tin khoản thu',
                 icon: <EditOutlined />,
                //  disabled: record.status !== "UNPAID" || Number(record.amount_paid) > 0 || collection.status === "PLANNED",
                 disabled: record.status !== "UNPAID" || record.status === "CANCELLED" || Number(record.amount_paid) > 0,
                 onClick: () => editCustomerFee(record),
             },
             {
                 key: 'invoice',
                 label: 'Lập hóa đơn',
                 icon: <FileTextOutlined />,
                 disabled: true,
                 onClick: () => handleExportInvoice(record.id, true),
             },
             ...(canDeleteFee
                 ? [
                     { type: 'divider' },
                     {
                         key: 'delete_fee',
                         label: 'Xóa khoản thu',
                         icon: <DeleteOutlined />,
                         danger: true,
                         // Cùng điều kiện với backend: chưa từng phát sinh tiền
                         disabled: ["PAID", "PARTIAL"].includes(record.status) || Number(record.amount_paid) > 0,
                         onClick: () => showConfirmDeleteCustomerFee(record),
                     },
                 ]
                 : []),
          ];

          return (
            <Space size="small">
              {/* Một nút, tự theo tình trạng: chưa nhận tin thì gửi luôn; đã nhận thì hỏi
                  trước khi gửi thêm tin. Cùng điều kiện "đã gửi" với backend (referentid). */}
              <Button
                  type="default"
                  size="small"
                  disabled={isLocked}
                  icon={record.referentid ? <ReloadOutlined /> : <SendOutlined />}
                  title={
                    record.referentid
                      ? `Đã gửi tin${record.last_sent_at ? ` lúc ${dayjs(record.last_sent_at).format("DD/MM/YYYY HH:mm")}` : ""} — bấm để gửi lại`
                      : "Gửi tin nhắc thanh toán"
                  }
                  onClick={() => (record.referentid ? showConfirmResendOne(record) : sendOne(record))}
                  loading={sendOneLoading === record.id}
              />
              <Button
                type="default"
                size="small"
                icon={<CreditCardOutlined />}
                onClick={() => pay(record)}
                title="Thanh toán"
                disabled={isLocked}
              />
              <Button
                type="default"
                size="small"
                icon={<PrinterOutlined />}
                onClick={() => handleExportOneReceipt(record.id)}
                title="Xuất phiếu thu"
                disabled={isLocked}
              />
              <Dropdown menu={{ items: dropdownMenuItems }} trigger={['click']}>
                  <Button size="small" type="text" icon={<div style={{ transform: 'rotate(90deg)', letterSpacing: '2px', fontWeight: 'bold' }}>...</div>} />
              </Dropdown>
            </Space>
          );
        },
      },
    ];
  };

  //
  const pay = (record) => {
    setSelectedCustomerFee(record);
    const totalAmount = (record.amount_due || 0) - (record.amount_paid || 0);

    payForm.setFieldsValue({
      transaction_amount: totalAmount,
      note: "",
    });

    setPayModalVisible(true);
  };

  const handlePayment = async (values) => {
    try {
      setPayLoading(true);

      const dataPay = {
        transaction_amount: values.transaction_amount,
        note: values.note || "",
      };

      await paymentAPIWithErrorHandling.payemntManual(
        selectedCustomerFee.id,
        dataPay
      );

      message.success("Thanh toán thành công!");
      setPayModalVisible(false);
      payForm.resetFields();

      // Refresh data
      fetchCollectionData();
      fetchCollectionStats();
    } catch (error) {
      message.error(error.message || "Thanh toán thất bại");
    } finally {
      setPayLoading(false);
    }
  };

  const sendOne = async (record) => {
    try {
      setSendOneLoading(record.id);
      // const response = await mapZnsAPIWithErrorHandling.sendOneZns(
      //   record.collection_id,
      //   record.customer_id
      // );

      const response = await mapZnsAPIWithErrorHandling.sendOneZns(
        record.id
      );
      message.success(
        response?.message ?? "Hệ thống ghi nhận. Đang chờ xử lý!"
      );
    } catch (error) {
      message.error(error.message);
    } finally {
      setSendOneLoading(null);
    }
  };

  const resendOne = async (record) => {
    try {
      setSendOneLoading(record.id);
      // const response = await mapZnsAPIWithErrorHandling.resendOneZns(
      //   record.collection_id,
      //   record.customer_id
      // );
      const response = await mapZnsAPIWithErrorHandling.resendOneZns(
        record.id
      );
      message.success(
        response?.message ?? "Hệ thống ghi nhận. Đang chờ xử lý!"
      );
    } catch (error) {
      message.error(error.message);
    } finally {
      setSendOneLoading(null);
    }
  };

  const showConfirmResendOne = async (record) => {
    const sentAt = record.last_sent_at
      ? ` lúc ${dayjs(record.last_sent_at).format("DD/MM/YYYY HH:mm")}`
      : "";
    const confirmed = await confirmAction({
      title: 'Gửi lại tin cho khách hàng này?',
      content: `Khách hàng đã nhận tin${sentAt}. Hệ thống sẽ gửi thêm một tin nữa.`,
      okText: 'Gửi lại',
    });
    if (!confirmed) return;

    resendOne(record);
  };

  const handleCancelInvoice = async (record) => {
    try {
      message.loading({ content: "Đang xử lý hủy giao dịch...", key: "cancelInvoice" });
      const response = await collectionAPIWithErrorHandling.cancelSingleTransaction(record.id);
      message.success({ content: response?.data?.message || "Hủy giao dịch thành công!", key: "cancelInvoice" });
      fetchCollectionData();
      fetchCollectionStats();
    } catch (error) {
      message.error({ content: error.message || "Hủy giao dịch thất bại", key: "cancelInvoice" });
    }
  };

  const handleCancelCustomerFee = async (record, cancelReason) => {
    try {
      message.loading({ content: "Đang hủy khoản thu...", key: "cancelFee" });
      const response = await collectionAPIWithErrorHandling.cancelCustomerFee(record.id, {
        cancel_reason: cancelReason,
      });
      message.success({ content: response?.data?.message || "Hủy khoản thu thành công!", key: "cancelFee" });
      fetchCollectionData();
      fetchCollectionStats();
    } catch (error) {
      message.error({ content: error.message || "Hủy khoản thu thất bại", key: "cancelFee" });
    }
  };

  const showConfirmCancelInvoice = (record) => {
    Modal.confirm({
      title: 'Xác nhận hủy giao dịch',
      content: `Giao dịch mã QR thanh toán của khách hàng ${record.customer?.name || ''} sẽ bị hủy. Bạn có chắc chắn không?`,
      okText: 'Hủy giao dịch',
      okType: 'danger',
      cancelText: 'Đóng',
      onOk: () => handleCancelInvoice(record),
    });
  };

  const showConfirmCancelCustomerFee = (record) => {
    let reason = "";
    // Modal.confirm không có cách nào để onOk chặn việc đóng modal mà không reject, và
    // antd thì re-throw rejection đó (ant-design#6183) nên dev overlay đổ màn đỏ
    // "cancel_reason_required" mỗi lần bấm OK với ô lý do trống. Khoá luôn nút OK tới khi
    // có lý do: vừa bỏ được throw, vừa rõ ràng hơn cho người dùng.
    const modal = Modal.confirm({
      title: "Xác nhận hủy khoản thu",
      content: (
        <div>
          <p>
            Khoản thu của khách hàng <b>{record.customer?.name || ""}</b> sẽ chuyển sang trạng thái <b>CANCELLED</b>.
          </p>
          <div style={{ marginTop: 8 }}>
            <Text strong>Lý do hủy</Text>
            <TextArea
              rows={3}
              placeholder="Nhập lý do hủy..."
              onChange={(e) => {
                reason = e.target.value;
                modal.update({ okButtonProps: { disabled: !reason.trim() } });
              }}
            />
          </div>
        </div>
      ),
      okText: "Hủy khoản thu",
      okType: "danger",
      okButtonProps: { disabled: true },
      cancelText: "Đóng",
      onOk: () => handleCancelCustomerFee(record, reason.trim()),
    });
  };

  const handleDeleteCustomerFee = async (record, deleteReason) => {
    try {
      message.loading({ content: "Đang xóa khoản thu...", key: "deleteFee" });
      const response = await collectionAPIWithErrorHandling.deleteCustomerFee(record.id, {
        delete_reason: deleteReason,
      });
      message.success({ content: response?.data?.message || "Xóa khoản thu thành công!", key: "deleteFee" });
      fetchCollectionData();
      fetchCollectionStats();
    } catch (error) {
      message.error({ content: error.message || "Xóa khoản thu thất bại", key: "deleteFee" });
    }
  };

  // Khớp DELETE_FEE_REASON_MIN ở backend
  const DELETE_REASON_MIN = 10;

  const showConfirmDeleteCustomerFee = (record) => {
    let reason = "";
    const sentAt = record.last_sent_at ? dayjs(record.last_sent_at).format("DD/MM/YYYY HH:mm") : null;
    const modal = Modal.confirm({
      title: "Xóa khoản thu",
      content: (
        <div>
          <p>
            Khoản thu <b>{record.code}</b> của khách hàng <b>{record.customer?.name || ""}</b> sẽ bị xóa khỏi
            đợt thu, không còn trong danh sách, thống kê và file xuất.
          </p>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Chỉ dùng cho khoản thu tạo nhầm hoặc bị trùng. Khoản thu không còn phải thu thì dùng Hủy khoản thu."
          />
          {sentAt && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message={`Khách hàng đã nhận tin lúc ${sentAt}. Nếu họ vẫn chuyển khoản theo mã cũ, tiền về sẽ không khớp khoản thu nào.`}
            />
          )}
          <Text strong>Lý do xóa</Text>
          <TextArea
            rows={3}
            placeholder={`Nhập lý do xóa (tối thiểu ${DELETE_REASON_MIN} ký tự)...`}
            onChange={(e) => {
              reason = e.target.value;
              modal.update({ okButtonProps: { disabled: reason.trim().length < DELETE_REASON_MIN } });
            }}
          />
        </div>
      ),
      okText: "Xóa khoản thu",
      okType: "danger",
      okButtonProps: { disabled: true },
      cancelText: "Đóng",
      onOk: () => handleDeleteCustomerFee(record, reason.trim()),
    });
  };

  // Handle navigation
  const handleBack = () => {
    // navigate("/collections");
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/collections/${id}`);
  };

  const handleDelete = async () => {
    try {
      const confirmed = await confirmDelete({ entity: "đợt thu", name: collection.name });
      if (!confirmed) return;

      await collectionAPIWithErrorHandling.deleteCollection(collection.id);
      message.success("Xóa đợt thu thành công");

      // Refresh data
      navigate("/collections");
    } catch (error) {
      message.error(error.message || "Không thể xóa đợt thu");
    }
  };

  // Handle reset
  const handleReset = () => {
    fetchCollectionData();
    fetchCollectionStats();
  };

  // Thêm sau hàm handleBack
  const handleExportInvoice = async (keyId, isOne = false) => {
    const messageKey = "invoice";

    try {
      message.loading({ key: messageKey, content: "Đang xuất hóa đơn..." });
      const res = isOne
        ? await invoiceAPIWithErrorHandling.export(keyId)
        : await invoiceAPIWithErrorHandling.exportAll(id);

      const { success, message: msg, data } = res.data;
      
      if (!success) {
        throw new Error(msg || "Xuất hóa đơn thất bại");
      }

      const successCount = data?.success ?? 0;
      const failedCount = data?.failed ?? 0;

      // ✅ Toàn bộ thất bại
      if (successCount === 0 && failedCount > 0) {
        message.error({
          key: messageKey,
          content: `Xuất hóa đơn thất bại (${failedCount} khoản thu không thể xuất)`,
          duration: 5,
        });
        return;
      }

      // ⚠️ Thành công 1 phần
      if (failedCount > 0) {
        message.warning({
          key: messageKey,
          content: `Xuất hóa đơn hoàn tất: thành công ${successCount}, thất bại ${failedCount}`,
          duration: 6,
        });
        return;
      }

      // 🎉 Thành công toàn bộ
      message.success({
        key: messageKey,
        content: `Xuất hóa đơn thành công (${successCount} khoản thu)`,
      });

    } catch (error) {
      message.error({
        key: messageKey,
        content: error.message || "Không thể xuất hóa đơn",
      });
    }
  };

  /**
   * In phiếu thu của MỘT khách hàng.
   *
   * Vẫn tải trực tiếp trong request: một phiếu thì nhanh, không cần hàng đợi.
   */
  const handleExportOneReceipt = async (feeId, preview = true) => {
    const messageKey = "export";
    try {
      message.loading({ content: "Đang xuất phiếu thu...", key: messageKey });
      const res = await receiptAPIWithErrorHandling.exportReceiptPdf(feeId);

      const isSuccess = downloadOrPreviewFile(res, "phieu-thu.pdf", preview);

      if (!isSuccess && preview) {
        message.warning({
          key: messageKey,
          content: "Vui lòng cho phép popup để xem trước file!",
          duration: 4,
        });
        return;
      }

      message.success({
        content: "Xuất phiếu thu thành công",
        key: messageKey,
        duration: 3,
      });
    } catch (error) {
      message.error({
        key: messageKey,
        content: error?.message || "Không thể xuất phiếu thu",
        duration: 4,
      });
    }
  };

  const downloadOrPreviewFile = (res, fileName = "document.pdf", preview = true) => {
    const blob = new Blob([res], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    let success = true;
    if (preview) {
      const newTab = window.open(url, "_blank");
      if (!newTab) {
        // message.warning("Vui lòng cho phép popup để xem trước file!");
        success = false;
      }
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
    }

    setTimeout(() => URL.revokeObjectURL(url), 30000);

    return success;
  };


  // Handle export/print
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response =
        await collectionAPIWithErrorHandling.exportCollectionByID(id);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const currentDate = new Date()
        .toISOString()
        .split("T")[0]
        .replace(/-/g, "_");
      const filename = `DOT_THU_${currentDate}.xlsx`;

      downloadBlob(blob, filename);
      message.success(`Tải xuống chi tiết đợt thu thành công!`);
    } catch (error) {
      message.error(error.message || "Không thể tải xuống danh sách đợt thu");
    } finally {
      setIsExporting(false);
    }
  };

  const handleOrgInfo = (record) => {
    message.info(`Chi tiết thông tin đơn vị: ${record.organization?.name}`);
    navigate(`/organizations/information/${record.organization_id}`);
  };
  
  const handleCusInfo = (record) => {
    message.info(`Chi tiết thông tin khách hàng: ${record.customer?.name}`);
    navigate(`/customers/information/${record.customer_id}`);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center"
        style={{
          display: "flex",
          justifyContent: "center",
          height: "80vh",
          alignItems: "center",
        }}>
        <Spin size="large" tip="Đang tải thông tin đợt thu..." />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center">
        <Empty description="Không tìm thấy đợt thu" />
      </div>
    );
  }

  // Payment Modal
  const PaymentModal = (
    <Modal
      title={
        <Space>
          <CreditCardOutlined className="text-green-600" />
          <span>Thanh toán</span>
        </Space>
      }
      open={payModalVisible}
      onCancel={() => {
        setPayModalVisible(false);
        payForm.resetFields();
        setSelectedCustomerFee(null);
      }}
      footer={null}
      width="35%"
    >
      {selectedCustomerFee && (
        <>
          <Descriptions column={2} size="small" className="mb-4">
            <Descriptions.Item label="Khách hàng" span={2}>
              <Text strong>{selectedCustomerFee.customer?.name}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Tổng tiền">
              <Text strong className="text-green-600">
                {Number(selectedCustomerFee.amount_due || 0).toLocaleString("vi-VN")}đ
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Đã thanh toán">
              <Text strong className="text-green-600">
                {Number(selectedCustomerFee.amount_paid || 0).toLocaleString("vi-VN")}đ
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Form form={payForm} layout="vertical" onFinish={handlePayment}>
            <Form.Item
              name="transaction_amount"
              label="Số tiền thanh toán"
              // Mặc định là thu ĐỦ phần còn lại (đặt sẵn ở hàm pay()). Chỉ tài khoản được
              // cấp quyền mới sửa được ô này để thu lẻ. Khoá ở đây chỉ là lớp ngoài —
              // PaymentAccountService::assertManualAmountAllowed() mới là chặn thật, vì
              // gọi thẳng API thì không đi qua giao diện.
              extra={
                !canEditPayAmount
                  ? "Tài khoản của bạn chỉ được thu đủ số còn lại của khoản thu này."
                  : undefined
              }
              rules={[
                { required: true, message: "Vui lòng nhập số tiền" },
                { type: "number", min: 0, message: "Số tiền phải lớn hơn 0" },
              ]}
            >
              <InputNumber
                className="w-full"
                min={0}
                disabled={!canEditPayAmount}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                placeholder="Nhập số tiền"
                suffix="₫"
                style={{ "width": "100%" }}
              />
            </Form.Item>

            <Form.Item name="note" label="Ghi chú">
              <TextArea
                rows={3}
                placeholder="Ghi chú về giao dịch (không bắt buộc)"
                maxLength={255}
                showCount
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Space className="w-full justify-end">
                <Button
                  onClick={() => {
                    setPayModalVisible(false);
                    payForm.resetFields();
                  }}
                >
                  Hủy
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={payLoading}
                  icon={<CreditCardOutlined />}
                >
                  Xác nhận thanh toán
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
      
  const getChargeTargetTypeTag = (charge_target_type) => {
      const statusConfig = {
      CUSTOMER: { color: "blue", text: "Khách hàng" },
      APARTMENT: { color: "green", text: "Chung cư" },
      VEHICLE: { color: "red", text: "Bãi xe" },
      };

      const config = statusConfig[charge_target_type] || { color: "default", text: charge_target_type };
      return <Tag color={config.color} className="px-3 py-1">{config.text}</Tag>;
  };

  const statusInfo = getStatusInfo();
  // const progressInfo = getProgressInfo();
  const tableColumns = getTableColumns();

  // Lấy từ backend, không tự trừ trên số tổng — xem chú thích ở mapCollectionStats.
  const outstanding = stats.totalOutstanding || 0;
  const overpaid = stats.totalOverpaid || 0;

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      {/* <Card className="shadow-lg max-w-7xl mx-auto"> */}
      <Card className="shadow-lg mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            separator=">"
            className="mb-4"
            items={[
              {
                href: "/",
                title: (
                  <>
                    <HomeOutlined /> Trang chủ
                  </>
                ),
              },
              {
                href: "/collections",
                title: (
                  <>
                    <DollarOutlined /> Đợt thu
                  </>
                ),
              },
              {
                title: (
                  <>
                    <InfoCircleOutlined /> Chi tiết
                  </>
                ),
              },
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-6">
          <Row justify="space-between" align="middle">
            <Col>
              <Space align="start">
                <div>
                  <Title level={2} className="mb-2" style={{ marginBottom: 4 }}>
                    {collection.name}
                  </Title>
                  <Space size={6} wrap>
                    <Tag
                      color={statusInfo.color}
                      icon={statusInfo.icon}
                      className="px-3 py-1"
                    >
                      {statusInfo.text}
                    </Tag>
                    {getChargeTargetTypeTag(collection.charge_target_type)}
                    <DeadlineTag collection={collection} />
                  </Space>
                </div>
              </Space>
            </Col>
            <Col className="mt-2">
              {/* Hai thao tác hay dùng để ngoài, phần còn lại vào "⋯". Nút "Xuất hóa đơn"
                  đang khóa cứng (disabled={true}) nên không đưa lên header nữa. */}
              <Space>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  className="shadow-sm"
                  disabled={!canEditCollection}
                >
                  Chỉnh sửa
                </Button>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => queueExport(id, "child")}
                  loading={exporting}
                  className="shadow-sm"
                  disabled={collection.status === 'PLANNED' || collection.status === 'CLOSED'}
                >
                  Xuất phiếu thu
                </Button>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      { key: "history", icon: <HistoryOutlined />, label: "Lịch sử xuất phiếu thu", onClick: openHistory },
                      {
                        key: "excel",
                        icon: <DownloadOutlined />,
                        label: isExporting ? "Đang xuất Excel..." : "Xuất Excel",
                        disabled: isExporting || collection.status === 'PLANNED',
                        onClick: handleExport,
                      },
                      { key: "reload", icon: <ReloadOutlined />, label: "Làm mới", onClick: handleReset },
                      { type: "divider" },
                      { key: "delete", icon: <DeleteOutlined />, label: "Xóa đợt thu", danger: true, disabled: !canDeleteCollection, onClick: handleDelete },
                    ],
                  }}
                >
                  <Button icon={<MoreOutlined />} className="shadow-sm" aria-label="Thao tác khác" />
                </Dropdown>
              </Space>
            </Col>
          </Row>
        </div>

        <OverduePaymentAlert collection={collection} onExtend={() => setExtendOpen(true)} />

        <Divider />

        {/* Overview Statistics */}
        <Spin spinning={statsLoading}>
          <Row gutter={[16, 16]} className="mb-6">
            {/* Card 1: Tổng khoản thu (một khách nhiều tài sản = nhiều khoản) */}
            <Col xs={12} sm={8} lg={4}>
              <Card
                className="shadow-sm h-full"
                style={{ borderTop: '3px solid #0070D7', borderRadius: 8 }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                  <TeamOutlined style={{ marginRight: 4, color: '#0070D7' }} />
                  Tổng khoản thu
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#0070D7', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                  {(stats.totalFees || 0).toLocaleString('vi-VN')}
                </div>
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                  {/* Cache SWR cũ chưa có số khách thì bỏ qua thay vì in "0 khách hàng" */}
                  {stats.totalUniqueCustomers > 0 && (
                    <div>{stats.totalUniqueCustomers.toLocaleString('vi-VN')} khách hàng</div>
                  )}
                  {stats.totalPaid || 0} khoản đã TT · {stats.totalPending || 0} chưa TT
                </div>
              </Card>
            </Col>

            {/* Card 2: Tổng phải thu */}
            <Col xs={12} sm={8} lg={5}>
              <Card
                className="shadow-sm h-full"
                style={{ borderTop: '3px solid #0070D7', borderRadius: 8 }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                  <DollarOutlined style={{ marginRight: 4, color: '#0070D7' }} />
                  Tổng phải thu
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#0070D7', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                  {(stats.totalAmountDue || 0).toLocaleString('vi-VN')} ₫
                </div>
              </Card>
            </Col>

            {/* Card 3: Đã thu được */}
            <Col xs={12} sm={8} lg={5}>
              <Card
                className="shadow-sm h-full"
                style={{ borderTop: '3px solid #128555', borderRadius: 8 }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                  <CheckCircleOutlined style={{ marginRight: 4, color: '#128555' }} />
                  Đã thu được
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#128555', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                  {(stats.totalAmountPaid || 0).toLocaleString('vi-VN')} ₫
                </div>
              </Card>
            </Col>

            {/* Card 4: Còn phải thu */}
            <Col xs={12} sm={8} lg={5}>
              <Card
                className="shadow-sm h-full"
                style={{
                  borderTop: `3px solid ${outstanding > 0 ? '#E5065C' : '#128555'}`,
                  borderRadius: 8,
                  background: outstanding > 0 ? '#fff7f7' : '#EAF9F2',
                }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                  <MinusCircleOutlined style={{
                    marginRight: 4,
                    color: outstanding > 0 ? '#E5065C' : '#128555'
                  }} />
                  Còn phải thu
                </div>
                <div style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: outstanding > 0 ? '#E5065C' : '#128555',
                  lineHeight: 1.3,
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {outstanding.toLocaleString('vi-VN')} ₫
                </div>
                {outstanding <= 0 && (
                  <div style={{ fontSize: 11, color: '#128555', marginTop: 2 }}>✓ Hoàn thành</div>
                )}
                {/* Chỉ hiện khi thực sự có tiền thu vượt — không thêm nhiễu vào màn khi = 0. */}
                {overpaid > 0 && (
                  <div style={{ fontSize: 11, color: '#A8681E', marginTop: 2 }}>
                    Thu vượt {overpaid.toLocaleString('vi-VN')} ₫
                    {stats.totalOverpaidCustomers > 0 && ` · ${stats.totalOverpaidCustomers} khoản`}
                  </div>
                )}
              </Card>
            </Col>

            {/* Card 5: Tỷ lệ thu */}
            <Col xs={24} sm={16} lg={5}>
              <Card
                className="shadow-sm h-full"
                style={{ borderTop: '3px solid #656DC7', borderRadius: 8 }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                  <BarChartOutlined style={{ marginRight: 4, color: '#656DC7' }} />
                  Tỷ lệ thu
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#656DC7', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                  {stats.collectionRate || 0}%
                </div>
                <Progress
                  percent={stats.collectionRate || 0}
                  strokeColor={{
                    '0%': '#E5065C',
                    '50%': '#A8681E',
                    '100%': '#128555',
                  }}
                  trailColor="#F1F5F9"
                  size="small"
                  showInfo={false}
                  style={{ marginTop: 6 }}
                />
              </Card>
            </Col>
          </Row>
        </Spin>

        {/* Collection Information */}
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={16}>
            <Card
              title={
                <span className="text-blue-600">
                  <CalendarOutlined className="mr-2" />
                  Thông tin đợt thu
                </span>
              }
              className="shadow-sm border-blue-200 h-full"
            >
              <Descriptions column={2} bordered size="middle">
                <Descriptions.Item span={2} label="Tên đợt thu">
                  <Text strong>{collection.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Ngày bắt đầu">
                  <Space>
                    <CalendarOutlined />
                    <Text>
                      {dayjs(collection.start_date).format("DD/MM/YYYY")}
                    </Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Ngày kết thúc">
                  <Space>
                    <CalendarOutlined />
                    <Text>
                      {dayjs(collection.end_date).format("DD/MM/YYYY")}
                    </Text>
                  </Space>
                </Descriptions.Item>
                {collection.payment_exp_date && (
                  <Descriptions.Item label="Hạn giao dịch (đã gia hạn)">
                    <Space>
                      <ClockCircleOutlined />
                      <Text>{dayjs(collection.payment_exp_date).format("DD/MM/YYYY")}</Text>
                    </Space>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Thời gian diễn ra">
                  <Text>
                    {dayjs(collection.end_date).diff(
                      dayjs(collection.start_date),
                      "day"
                    ) + 1}{" "}
                    ngày
                  </Text>
                </Descriptions.Item>

                {/* ✅ Giờ gửi tin nhắc nhở */}
                {/* <Descriptions.Item label="Giờ gửi tin nhắc">
                  <Space>
                    <ClockCircleOutlined />
                    <Text>
                      {collection.send_schedule_at
                        ? dayjs(collection.send_schedule_at, "HH:mm:ss").format("HH:mm:ss")
                        : "—"}
                    </Text>
                  </Space>
                </Descriptions.Item> */}

                {/* ✅ Số ngày sau hạn */}
                {/* <Descriptions.Item label="Sau hạn (ngày)">
                  <Text>{collection.after_date ?? "—"}</Text>
                </Descriptions.Item> */}

                {/* ✅ Số ngày trước hạn */}
                {/* <Descriptions.Item label="Trước hạn (ngày)">
                  <Text>{collection.before_date ?? "—"}</Text>
                </Descriptions.Item> */}

                {/* <Descriptions.Item label="T/K phát hành hóa đơn">
                  <Space>
                    <Text strong>
                      {collection?.invoice_cnf?.name}
                    </Text>
                  </Space>
                </Descriptions.Item> */}
                
                <Descriptions.Item label="Hạn thu(tin nhắn nhận)">
                  <Space>
                    <ClockCircleOutlined />
                    <Text>
                      {collection.send_date
                        ? dayjs(collection.send_date).format("DD/MM/YYYY")
                        : "—"}
                    </Text>
                  </Space>
                </Descriptions.Item>

                {/* {collection.description && ( */}
                  <Descriptions.Item span={2} label="Mô tả">
                    <Paragraph className="mt-2 p-3 bg-gray-50 rounded-lg">
                      {collection?.description}
                    </Paragraph>
                  </Descriptions.Item>
                {/* )} */}

              </Descriptions>

            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              title={
                <span className="text-green-600">
                  <BarChartOutlined className="mr-2" />
                  Thống kê thanh toán
                </span>
              }
              className="shadow-sm border-green-200 h-full"
            >
              <Spin spinning={statsLoading}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Đã thanh toán"
                      value={stats.totalPaid || 0}
                      suffix="khoản"
                      valueStyle={{ color: "#128555", fontSize: "20px" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Chưa thanh toán"
                      value={stats.totalPending || 0}
                      suffix="khoản"
                      valueStyle={{ color: "#A8681E", fontSize: "20px" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Đã hủy"
                      value={stats.totalCancelled || 0}
                      suffix="khoản"
                      valueStyle={{ color: "#64748B", fontSize: "20px" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Quá hạn"
                      value={stats.totalOverdue || 0}
                      suffix="khoản"
                      valueStyle={{ color: "#E5065C", fontSize: "20px" }}
                    />
                  </Col>
                </Row>
              </Spin>
            </Card>
          </Col>
        </Row>

        {/* Timeline */}
        {/* <Card
          className="mt-6 shadow-sm"
          title={
            <span className="text-purple-600">
              <ClockCircleOutlined className="mr-2" />
              Lịch sử thay đổi
            </span>
          }
        >
          <Timeline>
            <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
              <Text strong>Tạo đợt thu</Text>
              <br />
              <Text type="secondary">
                {dayjs(collection.created_at).format("DD/MM/YYYY HH:mm")}
              </Text>
            </Timeline.Item>

            {collection.updated_at !== collection.created_at && (
              <Timeline.Item color="blue" dot={<EditOutlined />}>
                <Text strong>Cập nhật lần cuối</Text>
                <br />
                <Text type="secondary">
                  {dayjs(collection.updated_at).format("DD/MM/YYYY HH:mm")}
                </Text>
              </Timeline.Item>
            )}

            <Timeline.Item color={statusInfo.color} dot={statusInfo.icon}>
              <Text strong>Trạng thái hiện tại</Text>
              <br />
              <Text type="secondary">{statusInfo.text}</Text>
            </Timeline.Item>
          </Timeline>
        </Card> */}

        {/* Customer Fees Detail Table */}
        <Card
          className="mt-6 shadow-sm"
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <span className="text-orange-600">
                <FileTextOutlined className="mr-2" />
                Chi tiết các khoản thu
              </span>

              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => setSendTarget({ id: collection.id, type: "collection" })}
                disabled={collection.status === "PLANNED"}
              >
                Gửi tin
              </Button>
            </Space>

            <Text type="secondary" style={{ fontSize: 14 }}>
              {/* Cùng nguồn với bảng bên dưới: N dòng = N khoản thu, M khách không trùng */}
              Tổng: <strong>{collection.customer_fees?.length || 0}</strong> khoản thu ·{" "}
              <strong>{new Set((collection.customer_fees || []).map((f) => f.customer_id)).size}</strong> khách hàng
            </Text>
            </div>
          }
        >
          {/* Filter & Search controls */}
          <Row gutter={[16, 12]} className="mb-4" align="middle">
            <Col xs={24} md={14}>
              {/* Status filter badges */}
              {(() => {
                const fees = collection.customer_fees || [];
                const countAll = fees.length;
                const countPaid = fees.filter(f => f.status === 'PAID').length;
                const countUnpaid = fees.filter(f => f.status === 'UNPAID').length;
                const countCancelled = fees.filter(f => f.status === 'CANCELLED').length;
                const countZero = fees.filter(isZeroFee).length;
                const btnStyle = (key) => ({
                  cursor: 'pointer',
                  padding: '4px 12px',
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: statusFilter === key ? 600 : 400,
                  border: statusFilter === key ? '1.5px solid' : '1.5px solid #CBD5E1',
                  background: statusFilter === key
                    ? (key === 'PAID' ? '#EAF9F2' : key === 'UNPAID' ? '#FFF5E8' : key === 'CANCELLED' ? '#F8FAFC' : '#e6f4ff')
                    : '#fff',
                  borderColor: statusFilter === key
                    ? (key === 'PAID' ? '#128555' : key === 'UNPAID' ? '#A8681E' : key === 'CANCELLED' ? '#64748B' : '#0070D7')
                    : '#CBD5E1',
                  color: statusFilter === key
                    ? (key === 'PAID' ? '#128555' : key === 'UNPAID' ? '#A8681E' : key === 'CANCELLED' ? '#475569' : '#0070D7')
                    : '#475569',
                  transition: 'all 0.2s',
                  userSelect: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                });
                return (
                  <Space size={8} wrap>
                    <span style={btnStyle('ALL')} onClick={() => setStatusFilter('ALL')}>
                      Tất cả <span style={{ background: '#0070D7', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11 }}>{countAll}</span>
                    </span>
                    <span style={btnStyle('PAID')} onClick={() => setStatusFilter('PAID')}>
                      <CheckCircleOutlined /> Đã TT <span style={{ background: '#16A66A', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11 }}>{countPaid}</span>
                    </span>
                    <span style={btnStyle('UNPAID')} onClick={() => setStatusFilter('UNPAID')}>
                      <ClockCircleOutlined /> Chưa TT <span style={{ background: '#FF9D2E', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11 }}>{countUnpaid}</span>
                    </span>
                    <span style={btnStyle('CANCELLED')} onClick={() => setStatusFilter('CANCELLED')}>
                      <MinusCircleOutlined /> Đã hủy <span style={{ background: '#64748B', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11 }}>{countCancelled}</span>
                    </span>
                    {/* Ẩn khi đợt không có khoản 0đ; vẫn hiện nếu đang bật để còn chỗ tắt. */}
                    {(countZero > 0 || zeroOnly) && (
                      <span
                        role="button"
                        tabIndex={0}
                        aria-pressed={zeroOnly}
                        title="Khoản phải thu 0đ (không tính khoản đã hủy) — lọc cộng thêm với trạng thái"
                        onClick={() => setZeroOnly(v => !v)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setZeroOnly(v => !v)}
                        style={{
                          ...btnStyle(null),
                          fontWeight: zeroOnly ? 600 : 400,
                          background: zeroOnly ? semantic.dangerSubtle : neutral[0],
                          borderColor: zeroOnly ? semantic.danger : neutral[300],
                          color: zeroOnly ? semantic.danger : neutral[600],
                        }}
                      >
                        <ExclamationCircleOutlined /> 0đ <span style={{ background: semantic.dangerSolid, color: neutral[0], borderRadius: 10, padding: '0 7px', fontSize: 11 }}>{countZero}</span>
                      </span>
                    )}
                  </Space>
                );
              })()}
            </Col>
            <Col xs={24} sm={10} md={8}>
              <Input
                placeholder="Tìm theo tên, SĐT hoặc tài sản..."
                prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Hiển thị: <strong>{filteredCustomerFees.length}</strong> / {collection.customer_fees?.length || 0}
              </Text>
            </Col>
          </Row>

          {collection.customer_fees && collection.customer_fees.length > 0 ? (
            <Table
              dataSource={filteredCustomerFees}
              columns={tableColumns}
              rowKey="id"
              size="small"
              pagination={{
                current: feePagination.current,
                pageSize: feePagination.pageSize,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
                showQuickJumper: true,
                // Đổi số dòng/trang thì về trang 1 cho khỏi lệch vị trí đang xem
                onChange: (current, pageSize) =>
                  setFeePagination((p) => ({
                    current: pageSize !== p.pageSize ? 1 : current,
                    pageSize,
                  })),
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} khoản thu`,
              }}
              scroll={{ x: 'max-content' }}
              className="rounded-lg"
              summary={(pageData) => {
                const sumDiscount = pageData.reduce((s, r) => s + Number(r.discount_override || 0), 0);
                const sumDue = pageData.reduce((s, r) => s + Number(r.amount_due || 0), 0);
                const sumPaid = pageData.reduce((s, r) => s + Number(r.amount_paid || 0), 0);
                const sumRemaining = Math.max(0, sumDue - sumPaid);
                // label span: Mã(1)+KH(1)+Đơn vị(1)+Tài sản(1)+fee_definitions(N) = 4+N
                // sau đó: Giảm trừ(1) | Tổng cộng(1) | Đã TT(1) | Còn lại(1) | Tài khoản thu+Trạng thái+Thao tác(3)
                const feeCount = collection.fee_definitions?.length || 0;
                // const labelColSpan = 4 + feeCount;
                const labelColSpan = 1;
                const emptyCellsCount = 3 + feeCount;
                return (
                  <Table.Summary.Row style={{ background: '#F8FAFC', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0} colSpan={labelColSpan}>
                      <Text strong style={{ color: '#475569' }}>
                        Tổng trang hiện tại ({pageData.length} khoản)
                      </Text>
                    </Table.Summary.Cell>
                    {/* Render các cell trống ở giữa để giữ border */}
                      {Array.from({ length: emptyCellsCount }).map((_, idx) => (
                        <Table.Summary.Cell key={`empty-${idx}`} />
                    ))}
                    {/* Giảm trừ */}
                    <Table.Summary.Cell align="right">
                      <Text style={{ color: '#64748B', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                        {sumDiscount > 0 ? `-${sumDiscount.toLocaleString('vi-VN')} ₫` : '—'}
                      </Text>
                    </Table.Summary.Cell>
                    {/* Tổng cộng */}
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ color: '#0070D7', fontVariantNumeric: 'tabular-nums' }}>
                        {sumDue.toLocaleString('vi-VN')} ₫
                      </Text>
                    </Table.Summary.Cell>
                    {/* Đã thanh toán */}
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ color: '#128555', fontVariantNumeric: 'tabular-nums' }}>
                        {sumPaid.toLocaleString('vi-VN')} ₫
                      </Text>
                    </Table.Summary.Cell>
                    {/* Còn lại */}
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ color: sumRemaining > 0 ? '#E5065C' : '#128555', fontVariantNumeric: 'tabular-nums' }}>
                        {sumRemaining > 0 ? `${sumRemaining.toLocaleString('vi-VN')} ₫` : '✓ Xong'}
                      </Text>
                    </Table.Summary.Cell>
                    {/* Tài khoản thu + Trạng thái + Thao tác */}
                    <Table.Summary.Cell colSpan={3} />
                  </Table.Summary.Row>
                );
              }}
            />
          ) : (
            <Empty
              description="Chưa có khoản thu nào trong đợt thu này"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>

        {/* Action Buttons */}
        <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-green-50">
          <Row justify="space-between" align="middle">
            <Col>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                size="default"
                className="shadow-sm"
              >
                Quay lại
              </Button>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleExportInvoice}
                  size="default"
                  className="shadow-sm"
                  // disabled={collection.status === 'PLANNED'}
                  disabled={true}
                >
                  Lập nhiều hóa đơn
                </Button>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => queueExport(id, "child")}
                  loading={exporting}
                  size="default"
                  className="shadow-sm"
                  disabled={collection.status === 'PLANNED' || collection.status === 'CLOSED'}
                >
                  Xuất phiếu thu
                </Button>
                <Button
                  icon={<HistoryOutlined />}
                  onClick={openHistory}
                  size="default"
                  className="shadow-sm"
                >
                  Lịch sử xuất phiếu thu
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  loading={isExporting}
                  size="default"
                  className="shadow-sm"
                  disabled={collection.status === 'PLANNED'}
                >
                  Xuất Excel
                </Button>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  size="default"
                  className="shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                  disabled={!canEditCollection}
                >
                  Chỉnh sửa
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  className="shadow-sm"
                  disabled={!canDeleteCollection}
                >
                  Xóa
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  Làm mới
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      </Card>
      {payModalVisible && PaymentModal}
      
      <Modal
        title="Sửa thông tin khoản thu"
        open={editFeeModalVisible}
        onCancel={() => setEditFeeModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setEditFeeModalVisible(false)}>Hủy</Button>,
          <Button key="submit" type="primary" loading={editFeeLoading} onClick={() => editFeeForm.submit()}>Lưu thay đổi</Button>
        ]}
        width="min(860px, 96vw)"
        styles={{ body: { maxHeight: "68vh", overflowY: "auto", paddingRight: 8 } }}
      >
        <Form
          form={editFeeForm}
          layout="vertical"
          onFinish={handleEditFeeSubmit}
        >
          {/* Hai cột: form một cột ở bề rộng 500px đẩy phần tổng tiền xuống dưới
              vùng nhìn thấy, người dùng phải cuộn mới biết mình vừa sửa thành bao nhiêu. */}
          <Divider orientation="left" plain style={{ marginTop: 0 }}>Thông tin tài sản</Divider>

          <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 12 }}>
            Ba trường dưới đây chỉ đổi trong đợt thu này, không ghi ngược lên hồ sơ tài sản gốc.
          </Text>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Tên tài sản" name="charge_target_number">
                <Input placeholder="Ví dụ: Phòng 101, Biển số 30A-123.45" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Đường" name="charge_target_number_one">
                <Input placeholder="Tên đường" maxLength={255} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Chi tiết" name="charge_target_number_two">
                <Input.TextArea placeholder="Chi tiết tài sản/phòng..." rows={2} maxLength={500} showCount />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>Chi tiết khoản thu</Divider>

          <Row gutter={16}>
            {collection?.fee_definitions?.map((fd) => (
              <Col xs={24} md={12} key={fd.id}>
                <Form.Item label={`${fd.name} (Thuế: ${fd.tax || 0}% | Đơn vị: ${fd.unit || ''})`} name={["fees", fd.id]}>
                  <InputNumber
                    style={{ width: "100%" }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => value.replace(/\$\s?|(\.*)/g, "").replace(/,/g, "")}
                    min={0}
                  />
                </Form.Item>
              </Col>
            ))}
            <Col xs={24} md={12}>
              <Form.Item label="Giảm trừ (VNĐ)" name="discount_override">
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(\.*)/g, "").replace(/,/g, "")}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#EAF9F2', padding: '10px 15px', borderRadius: '8px', border: '1px solid #B8E8D0' }}>
            <Text strong style={{ fontSize: '16px' }}>Tổng cộng:</Text>
            <Text strong style={{ fontSize: '18px', color: '#128555' }}>{editFeePreviewTotal.toLocaleString("vi-VN")} ₫</Text>
          </div>
        </Form>
      </Modal>

      {/* {openModalHistorySendMessage &&
        <ModalHistorySendMessage openModal={openModalHistorySendMessage} handleCancel={() => setOpenModalHistorySendMessage(false)} customerFeeId={customerFeeId} />
      } */}

      {historyModal}

      <SendZnsDialog target={sendTarget} onClose={() => setSendTarget(null)} />
      <ExtendPaymentDialog
        open={extendOpen}
        collection={collection}
        onClose={() => setExtendOpen(false)}
        onDone={() => {
          fetchCollectionData();
          fetchCollectionStats();
        }}
      />
    </div>
  );
}
